// frontend/src/components/WhatsNewModal.jsx
// Modal showing release notes for the current version with rich text support
// Fetches current version dynamically from the API

import React, { useState, useEffect } from "react";
import secureAxios from "../utils/secureAxios";
import RichTextDisplay from "./common/RichTextDisplay";
import "../assets/css/rich-text-editor.css";

const WhatsNewModal = ({ show, onHide }) => {
  const [releaseNotes, setReleaseNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [availableVersions, setAvailableVersions] = useState([]);

  // Fetch current version from API when modal opens
  useEffect(() => {
    if (show) {
      fetchCurrentVersion();
      fetchAvailableVersions();
    }
  }, [show]);

  // Fetch release notes when selected version changes
  useEffect(() => {
    if (show && selectedVersion) {
      fetchReleaseNotes();
    }
  }, [show, selectedVersion]);

  const fetchCurrentVersion = async () => {
    try {
      const response = await secureAxios.get("/api/version");
      const version = response.data.current_version || "1.0.0";
      setCurrentVersion(version);
      setSelectedVersion(version); // Default to current version
    } catch (err) {
      console.error("Error fetching current version:", err);
      // Fallback to first available version
      setCurrentVersion(null);
    }
  };

  const fetchReleaseNotes = async () => {
    if (!selectedVersion) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await secureAxios.get(`/api/announcements/release-notes`, {
        params: { version: selectedVersion }
      });
      setReleaseNotes(response.data.announcements || []);
      
      // Mark these as viewed
      if (response.data.announcements?.length > 0) {
        response.data.announcements.forEach(note => {
          secureAxios.post(`/api/announcements/${note.announcement_id}/viewed`).catch(() => {});
        });
      }
    } catch (err) {
      console.error("Error fetching release notes:", err);
      setError("Failed to load release notes");
      setReleaseNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableVersions = async () => {
    try {
      const response = await secureAxios.get("/api/announcements/versions");
      setAvailableVersions(response.data.versions || []);
    } catch (err) {
      console.error("Error fetching versions:", err);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "feature":
        return "bi-star-fill text-warning";
      case "bugfix":
        return "bi-bug-fill text-danger";
      case "maintenance":
        return "bi-tools text-info";
      case "release_notes":
        return "bi-journal-text text-primary";
      default:
        return "bi-megaphone text-secondary";
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case "feature":
        return "New Feature";
      case "bugfix":
        return "Bug Fix";
      case "maintenance":
        return "Maintenance";
      case "release_notes":
        return "Release Notes";
      default:
        return "Announcement";
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "urgent":
        return "bg-danger";
      case "high":
        return "bg-warning text-dark";
      case "normal":
        return "bg-info text-dark";
      default:
        return "bg-secondary";
    }
  };

  if (!show) return null;

  return (
    <div 
      className="modal fade show d-block" 
      tabIndex="-1" 
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={(e) => e.target === e.currentTarget && onHide()}
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">
          {/* Header */}
          <div className="modal-header bg-primary text-white">
            <div>
              <h5 className="modal-title mb-0">
                <i className="bi bi-stars me-2"></i>
                What's New
              </h5>
              <small className="opacity-75">
                {currentVersion ? `Version ${currentVersion}` : "Loading..."}
              </small>
            </div>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={onHide}
              aria-label="Close"
            ></button>
          </div>

          {/* Version selector */}
          {availableVersions.length > 1 && (
            <div className="px-3 py-2 bg-light border-bottom">
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 small text-muted">Version:</label>
                <select 
                  className="form-select form-select-sm" 
                  style={{ width: "auto" }}
                  value={selectedVersion || ""}
                  onChange={(e) => setSelectedVersion(e.target.value)}
                >
                  {availableVersions.map(v => (
                    <option key={v} value={v}>
                      {v} {v === currentVersion ? "(Current)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Body */}
          <div className="modal-body">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2 text-muted">Loading release notes...</p>
              </div>
            ) : error ? (
              <div className="alert alert-danger">
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </div>
            ) : releaseNotes.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-journal-x display-4 text-muted"></i>
                <p className="mt-3 text-muted">
                  No release notes available for version {selectedVersion}
                </p>
              </div>
            ) : (
              <div className="release-notes-list">
                {releaseNotes.map((note, index) => (
                  <div 
                    key={note.announcement_id} 
                    className={`release-note-item ${index < releaseNotes.length - 1 ? "border-bottom pb-3 mb-3" : ""}`}
                  >
                    {/* Note header */}
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${getTypeIcon(note.announcement_type)}`}></i>
                        <span className="badge bg-light text-dark">
                          {getTypeLabel(note.announcement_type)}
                        </span>
                        {note.priority !== "normal" && (
                          <span className={`badge ${getPriorityBadge(note.priority)}`}>
                            {note.priority}
                          </span>
                        )}
                      </div>
                      <small className="text-muted">
                        {new Date(note.created_at).toLocaleDateString()}
                      </small>
                    </div>
                    
                    {/* Note title */}
                    <h6 className="mb-2">{note.title}</h6>
                    
                    {/* Note content - Now using RichTextDisplay */}
                    <RichTextDisplay content={note.content} />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <a 
              href="/crm/whats-new" 
              className="btn btn-outline-primary"
              onClick={onHide}
            >
              <i className="bi bi-clock-history me-1"></i>
              View All History
            </a>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={onHide}
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsNewModal;